import { trackedObject } from '@ember/reactive/collections';
import Route from '@ember/routing/route';
import type Transition from '@ember/routing/transition';
import { type Registry as Services, service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import dayjs from 'dayjs';
import { task, type TaskInstance } from 'ember-concurrency';
import type { Insightpagesfavourite } from 'frontend-utils/models';
import type {
  Event,
  Holiday,
  Occupancy,
  Period,
  ShortTermRentalBreakdown,
  ShortTermRentalConfig,
  ShortTermRentalKpi,
  ShortTermRentalProperty,
  ShortTermRentalRate,
} from 'lumen/models';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import type { Transition as TransitionWithTo } from 'lumen/services/transition';
import {
  APIGATEWAY,
  type DateAggregationType,
} from 'lumen/utils/constants/general-constants';
import {
  DEFAULT_NUMBER_OF_DAYS,
  NUMBER_OF_HISTORICAL_DAYS,
} from 'lumen/utils/constants/short-term-rental-constants';
import getAPIErrorMessage from 'lumen/utils/get-api-error-message';
import { hash } from 'rsvp';

export type Filters = {
  breakdown_filters: string[];
  price_range_end: number;
  price_range_start: number;
  radius: number;
};

type Model = {
  breakdowns: Partial<TaskInstance<ShortTermRentalBreakdown>>;
  config: ShortTermRentalConfig | null;
  defaultFilters: Insightpagesfavourite[] | null;
  endDate: string | null;
  evolutions: Partial<
    TaskInstance<{
      events: Event[];
      holidays?: Holiday[];
      occupancies?: Occupancy[];
      periods: Period[];
      shortTermRentalRates: ShortTermRentalRate[];
    }>
  >;
  kpis: Partial<TaskInstance<ShortTermRentalKpi[]>>;
  map: Partial<TaskInstance<ShortTermRentalProperty>>;
  startDate: string | null;
};

type Params = {
  activeFilters: string[];
  dateAggregation: DateAggregationType;
  fromDate: string | null;
  priceRangeEnd: number | null;
  priceRangeStart: number | null;
  radius: number | null;
  refDaysAgo: number;
  toDate: string | null;
};

export default class HotelShortTermRentalRoute extends Route {
  queryParams = {
    fromDate: {
      refreshModel: true,
    },
    toDate: {
      refreshModel: true,
    },
    radius: {
      refreshModel: true,
    },
    dateAggregation: {
      refreshModel: true,
    },
    refDaysAgo: {
      refreshModel: true,
    },
    activeFilters: {
      refreshModel: true,
      type: 'array',
    },
    priceRangeStart: {
      refreshModel: true,
      type: 'number',
    },
    priceRangeEnd: {
      refreshModel: true,
      type: 'number',
    },
  };

  @service declare access: Services['access'];
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];
  @service declare router: Services['router'];
  @service declare store: Services['store'];
  @service declare transition: Services['transition'];

  @tracked modelObject = trackedObject<Model>({
    breakdowns: { isRunning: true },
    config: null,
    defaultFilters: null,
    endDate: null,
    evolutions: { isRunning: true },
    kpis: { isRunning: true },
    map: { isRunning: true },
    startDate: null,
  });

  lastDateAggregation: DateAggregationType | null = null;
  lastFilters: string[] = [];
  lastFromDate: string | null = null;
  lastRadius: number | null = null;
  lastRefDaysAgo: number | null = null;
  lastSubscriptionId: string | null = null;
  lastToDate: string | null = null;

  requestBreakdownsTask = task(
    { restartable: true },
    async ({
      filters,
      subscription_id,
    }: {
      filters: string[];
      subscription_id: string;
    }) => {
      try {
        return await this.store.query<ShortTermRentalBreakdown>(
          'short-term-rental-breakdown',
          {
            filters,
            namespace: 'api/v3',
            subscription_id,
            typeKey: 'shorttermrental/breakdowns',
          },
        );
      } catch (error) {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.alerting.showErrorAlert({
          text: getAPIErrorMessage(error, this.intl),
        });
        this.errorTracking.captureException(error);
      }

      return null;
    },
  );

  requestConfigDataTask = task(
    { restartable: true },
    async (hotelId: string) => {
      try {
        return await this.store.findRecord<ShortTermRentalConfig>(
          'short-term-rental-config',
          hotelId,
          {
            adapterOptions: {
              namespace: 'api/v3',
              typeKey: `shorttermrental/configs`,
            },
          },
        );
      } catch (error) {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.alerting.showErrorAlert({
          text: getAPIErrorMessage(error, this.intl),
        });
        this.errorTracking.captureException(error);
      }

      return null;
    },
  );

  requestDefaultFiltersTask = task(
    { restartable: true },
    async (hotelId: string) => {
      try {
        return await this.store.query<Insightpagesfavourite>(
          'insightpagesfavourite',
          {
            hotel: hotelId,
            namespace: 'api/v3',
            page_type: 'RENTALS',
            typeKey: 'insightpagesfavourites',
          },
        );
      } catch (error) {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.alerting.showErrorAlert({
          text: getAPIErrorMessage(error, this.intl),
        });
        this.errorTracking.captureException(error);
      }

      return null;
    },
  );

  requestEvolutionChartDataTask = task(
    { restartable: true },
    async ({
      compareDate,
      dateAggregation,
      filters,
      offset,
      rangeEnd,
      rangeStart,
      singleDaySelected,
    }: {
      compareDate: string;
      dateAggregation: DateAggregationType;
      filters: string[];
      offset: number;
      rangeEnd: string;
      rangeStart: string;
      singleDaySelected: boolean;
    }) => {
      const hotelId = this.hotelAndMarketService.hotel.id;

      const eventParams = {
        from_date_range_end: rangeEnd,
        from_date_range_start: rangeStart,
        hotel_id: hotelId,
        namespace: APIGATEWAY,
        typekey: 'events',
      };

      const holidayParams = {
        from_date_range_end: rangeEnd,
        from_date_range_start: rangeStart,
        hotel_id: hotelId,
        namespace: APIGATEWAY,
        typekey: 'holidays',
      };

      try {
        if (singleDaySelected) {
          const occupancyParams = {
            date_aggregation: dateAggregation,
            from_date: rangeStart,
            namespace: APIGATEWAY,
            nr_of_days: NUMBER_OF_HISTORICAL_DAYS,
            offset,
            subscription_id: hotelId,
            typeKey: 'revenueinsight/occupancies/evolutions',
          };

          const periodParams = {
            compare_extract_date: compareDate,
            date_aggregation: dateAggregation,
            from_date: rangeStart,
            hotel_id: hotelId,
            los: this.hotelAndMarketService.hotel.defaultLosRates[0],
            meta: 'nested',
            namespace: APIGATEWAY,
            nr_days: NUMBER_OF_HISTORICAL_DAYS, // TODO: remove when BE supports `nr_of_days`
            nr_of_days: NUMBER_OF_HISTORICAL_DAYS,
            offset: 0,
            typeKey: 'rates/historical',
            with_comps: false,
          };

          const shortTermRentalRateParams = {
            date_aggregation: dateAggregation,
            filters,
            from_date: rangeStart,
            hotel_id: hotelId,
            namespace: APIGATEWAY,
            nr_of_days: NUMBER_OF_HISTORICAL_DAYS,
            offset,
            typeKey: 'shorttermrental/fromdates/evolutions',
          };

          if (this.access.hasOTB) {
            return await hash({
              events: this.store.query<Event>('event', eventParams),
              periods: this.store.query<Period>('period', periodParams),
              shortTermRentalRates: this.store.query<ShortTermRentalRate>(
                'short-term-rental-rate',
                shortTermRentalRateParams,
              ),
              occupancies: this.store.query<Occupancy>(
                'occupancy',
                occupancyParams,
              ),
            });
          }

          return await hash({
            periods: this.store.query<Period>('period', periodParams),
            shortTermRentalRates: this.store.query<ShortTermRentalRate>(
              'short-term-rental-rate',
              shortTermRentalRateParams,
            ),
            events: this.store.query<Event>('event', eventParams),
          });
        }

        const occupancyParams = {
          compare_extract_date: compareDate,
          date_aggregation: dateAggregation,
          from_date_range_end: rangeEnd,
          from_date_range_start: rangeStart,
          namespace: APIGATEWAY,
          subscription_id: hotelId,
          typeKey: 'revenueinsight/occupancies',
        };

        const periodParams = {
          compare_extract_date: compareDate,
          date_aggregation: dateAggregation,
          from_date_range_end: rangeEnd,
          from_date_range_start: rangeStart,
          hotel_id: hotelId,
          los: this.hotelAndMarketService.hotel.defaultLosRates[0],
          meta: 'nested',
          namespace: APIGATEWAY,
          typeKey: 'rates',
          with_comps: false,
        };

        const shortTermRentalRateParams = {
          compare_extract_date: compareDate,
          date_aggregation: dateAggregation,
          filters,
          from_date_range_end: rangeEnd,
          from_date_range_start: rangeStart,
          hotel_id: hotelId,
          namespace: APIGATEWAY,
          typeKey: 'shorttermrental/fromdates',
        };

        if (this.access.hasOTB) {
          return await hash({
            events: this.store.query<Event>('event', eventParams),
            holidays: this.store.query<Holiday>('holiday', holidayParams),
            occupancies: this.store.query<Occupancy>(
              'occupancy',
              occupancyParams,
            ),
            periods: this.store.query<Period>('period', periodParams),
            shortTermRentalRates: this.store.query<ShortTermRentalRate>(
              'short-term-rental-rate',
              shortTermRentalRateParams,
            ),
          });
        }

        return await hash({
          periods: this.store.query<Period>('period', periodParams),
          shortTermRentalRates: this.store.query<ShortTermRentalRate>(
            'short-term-rental-rate',
            shortTermRentalRateParams,
          ),
          events: this.store.query<Event>('event', eventParams),
          holidays: this.store.query<Holiday>('holiday', holidayParams),
        });
      } catch (error) {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.alerting.showErrorAlert({
          text: getAPIErrorMessage(error, this.intl),
        });
        this.errorTracking.captureException(error);

        return null;
      }
    },
  );

  requestKpisTask = task(
    { restartable: true },
    async ({
      compareDate,
      filters,
      fromDate,
      hotelId,
      toDate,
    }: {
      compareDate: string;
      filters: string[];
      fromDate: string;
      hotelId: string;
      toDate: string;
    }) => {
      const sharedKpis = {
        namespace: APIGATEWAY,
        from_date_range_start: fromDate,
        from_date_range_end: toDate,
        compare_extract_date: compareDate,
        filters,
      };

      const occupancyOwnHotelKpis = {
        ...sharedKpis,
        typeKey: 'revenueinsight/shorttermrental/kpis',
        subscription_id: hotelId,
        filters,
      };

      const rentalKpis = {
        ...sharedKpis,
        typeKey: 'shorttermrental/dashboard/kpis',
        hotel_id: hotelId,
        filters,
      };

      const propertiesKpis = {
        namespace: 'api/v3',
        typeKey: 'shorttermrental/properties/kpis',
        subscription_id: hotelId,
        filters,
      };

      const ratesKpis = {
        ...sharedKpis,
        typeKey: 'rates/shorttermrental/kpis',
        hotel_id: hotelId,
        los: this.hotelAndMarketService.hotel.defaultLosRates[0],
        filters,
      };

      const requestHash = [
        this.store.query<ShortTermRentalKpi>(
          'short-term-rental-kpi',
          rentalKpis,
        ),
        this.store.query<ShortTermRentalKpi>(
          'short-term-rental-kpi',
          propertiesKpis,
        ),
        this.store.query<ShortTermRentalKpi>(
          'short-term-rental-kpi',
          ratesKpis,
        ),
      ];

      if (this.access.hasOTB) {
        requestHash.push(
          this.store.query<ShortTermRentalKpi>(
            'short-term-rental-kpi',
            occupancyOwnHotelKpis,
          ),
        );
      }

      try {
        return await Promise.all(requestHash);
      } catch (error) {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.alerting.showErrorAlert({
          text: getAPIErrorMessage(error, this.intl),
        });
        this.errorTracking.captureException(error);
      }
      return null;
    },
  );

  requestMapDataTask = task(
    { restartable: true },
    async ({
      filters,
      subscription_id,
    }: {
      filters: string[];
      subscription_id: string;
    }) => {
      try {
        return await this.store.query<ShortTermRentalProperty>(
          'short-term-rental-property',
          {
            filters,
            namespace: 'api/v3',
            subscription_id,
            typeKey: 'shorttermrental/properties',
          },
        );
      } catch (error) {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.alerting.showErrorAlert({
          text: getAPIErrorMessage(error, this.intl),
        });
        this.errorTracking.captureException(error);
      }

      return null;
    },
  );

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hotel() {
    return this.hotelAndMarketService.hotelOrMarket;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get otaId() {
    return this.hotelAndMarketService.hotel?.OTAsRates?.[0]?.id;
  }

  beforeModel(transition: TransitionWithTo): void {
    const currentHotelId = this.hotelAndMarketService.hotel.id!;

    if (this.lastSubscriptionId && this.lastSubscriptionId !== currentHotelId) {
      this.resetModelObject();

      this.router.transitionTo({
        queryParams: {},
      });
    }

    this.lastSubscriptionId = currentHotelId;

    if (!this.access.hasShortTermRentalAccess) {
      this.transition.redirectToEnabledTab(transition);
      return;
    }

    this.transition.validateShortTermRentalQueryParams(
      transition,
      this.paramsFor(this.routeName), // Use paramsFor to obtain the deserialised query params
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  async model(params: Params, transition: Transition) {
    const { dateAggregation, fromDate, refDaysAgo, toDate } = params;

    const rangeStart =
      fromDate ??
      this.hotelAndMarketService.getLocalTime().format('YYYY-MM-DD');
    const rangeEnd =
      toDate ??
      this.hotelAndMarketService
        .getLocalTime()
        .add(DEFAULT_NUMBER_OF_DAYS, 'day')
        .format('YYYY-MM-DD');

    const today = this.hotelAndMarketService
      .getLocalTime()
      .format('YYYY-MM-DD');
    const offset = Math.max(dayjs(rangeStart).diff(dayjs(today), 'day'), 0);
    const daysSelected = dayjs(rangeEnd).diff(dayjs(rangeStart), 'day');
    const compareDate = this.hotelAndMarketService
      .getLocalTime()
      .subtract(refDaysAgo, 'day')
      .format('YYYY-MM-DD');

    const singleDaySelected = daysSelected === 0;

    // Subtract 1 day from the amount of days we want to go back in time, because today counts also as a day
    const startDate = singleDaySelected
      ? dayjs(rangeStart, 'YYYY-MM-DD')
          .subtract(NUMBER_OF_HISTORICAL_DAYS - 1 + offset, 'day')
          .format('YYYY-MM-DD')
      : rangeStart;

    this.modelObject.startDate = startDate;
    this.modelObject.endDate = rangeEnd;

    if (!this.modelObject.config) {
      const config = await this.requestConfigDataTask.perform(
        this.hotelAndMarketService.hotel.id!,
      );

      this.modelObject.config = config;
    }

    this.modelObject.defaultFilters =
      await this.requestDefaultFiltersTask.perform(
        this.hotelAndMarketService.hotel.id!,
      );

    const {
      radius: defaultRadius,
      price_range_start: defaultPriceRangeStart,
      price_range_end: defaultPriceRangeEnd,
      breakdown_filters: defaultBreakdownFilters,
    } = this.modelObject.defaultFilters![0]!.filters as unknown as Filters;

    const { radiusLimit, priceRangeLimitMin, priceRangeLimitMax } =
      this.modelObject.config!;

    const radius = params.radius ?? defaultRadius ?? radiusLimit;
    const priceRangeStart =
      params.priceRangeStart ?? defaultPriceRangeStart ?? priceRangeLimitMin;
    const priceRangeEnd =
      params.priceRangeEnd ?? defaultPriceRangeEnd ?? priceRangeLimitMax;

    const activeFilters = params.activeFilters ?? defaultBreakdownFilters;

    const allFilters = [
      ...activeFilters,
      `radius.${radius}`,
      `price_range_start.${priceRangeStart}`,
      `price_range_end.${priceRangeEnd}`,
    ];

    const hasRouteChanged =
      transition.from &&
      !transition.from.name.startsWith('hotel.short-term-rental');

    const hasDateOrReferenceOrRouteOrFiltersChange =
      this.lastRefDaysAgo !== refDaysAgo ||
      this.lastFromDate !== fromDate ||
      this.lastToDate !== toDate ||
      hasRouteChanged ||
      JSON.stringify(this.lastFilters) !== JSON.stringify(allFilters);

    if (hasDateOrReferenceOrRouteOrFiltersChange) {
      this.lastRefDaysAgo = refDaysAgo;
      this.lastFromDate = fromDate;
      this.lastToDate = toDate;
      this.lastFilters = [...allFilters];
    }

    if (
      this.lastRadius !== radius ||
      hasDateOrReferenceOrRouteOrFiltersChange
    ) {
      // @ts-expect-error: Incorrect type
      this.modelObject.map = this.requestMapDataTask.perform({
        filters: allFilters,
        subscription_id: this.hotelAndMarketService.hotel.id!,
      });

      this.lastRadius = radius;
    }

    if (
      this.lastDateAggregation !== dateAggregation ||
      hasDateOrReferenceOrRouteOrFiltersChange
    ) {
      // eslint-disable-next-line @typescript-eslint/await-thenable
      await this.store.unloadAll('event');

      // @ts-expect-error: Incorrect type
      this.modelObject.evolutions = this.requestEvolutionChartDataTask.perform({
        compareDate,
        dateAggregation,
        filters: allFilters,
        offset,
        rangeStart,
        rangeEnd,
        singleDaySelected,
      });

      this.lastDateAggregation = dateAggregation;
    }

    if (hasDateOrReferenceOrRouteOrFiltersChange) {
      // @ts-expect-error: Incorrect type
      this.modelObject.kpis = this.requestKpisTask.perform({
        hotelId: this.hotelAndMarketService.hotel.id!,
        fromDate: rangeStart,
        toDate: rangeEnd,
        compareDate,
        filters: allFilters,
      });

      // @ts-expect-error: Incorrect type
      this.modelObject.breakdowns = this.requestBreakdownsTask.perform({
        filters: allFilters,
        subscription_id: this.hotelAndMarketService.hotel.id!,
      });
    }

    return this.modelObject;
  }

  resetModelObject(): void {
    this.modelObject = trackedObject({
      breakdowns: { isRunning: true },
      config: null,
      defaultFilters: null,
      endDate: null,
      evolutions: { isRunning: true },
      kpis: { isRunning: true },
      map: { isRunning: true },
      startDate: null,
    });
  }
}
